import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';

const BASE = process.env.REACT_APP_API_URL || '';

const RAPI = (path, opts = {}) => {
  const token = localStorage.getItem('resellerToken');
  return fetch(`${BASE}${path}`, { ...opts, headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}`, ...opts.headers } });
};

const ResellerPanel = () => {
  const [reseller, setReseller] = useState(null);
  const [loginForm, setLoginForm] = useState({ email: '', password: '' });
  const [loginError, setLoginError] = useState('');
  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [tab, setTab] = useState('home');
  const [orderForm, setOrderForm] = useState({ serverInvite: '', productId: '', quantity: 14 });
  const [orderLoading, setOrderLoading] = useState(false);
  const [orderResult, setOrderResult] = useState(null);
  const [lastOrderId, setLastOrderId] = useState(null);
  const [showApiKey, setShowApiKey] = useState(false);
  const [keyCopied, setKeyCopied] = useState(false);
  const [topups, setTopups] = useState([]);
  const [topAmt, setTopAmt] = useState('');
  const [topMethod, setTopMethod] = useState('stripe');
  const [topCrypto, setTopCrypto] = useState('LTC');
  const [topMsg, setTopMsg] = useState(null);
  const [topLoading, setTopLoading] = useState(false);
  const navigate = useNavigate();

  const loadProfile = useCallback(async () => {
    try {
      const res = await RAPI('/api/reseller/me');
      if (!res.ok) throw new Error();
      setReseller(await res.json());
    } catch {
      setReseller(null);
      localStorage.removeItem('resellerToken');
    }
  }, []);

  const loadProducts = useCallback(async () => {
    try {
      const res = await RAPI('/api/reseller/products');
      if (res.ok) {
        const data = await res.json();
        setProducts(data.products || []);
      }
    } catch {}
  }, []);

  const loadOrders = useCallback(async () => {
    try {
      const res = await RAPI('/api/reseller/orders');
      if (res.ok) setOrders(await res.json());
    } catch {}
  }, []);

  const loadTopups = useCallback(async () => {
    try {
      const res = await RAPI('/api/reseller/topups');
      if (res.ok) setTopups(await res.json());
    } catch {}
  }, []);

  useEffect(() => {
    const t = localStorage.getItem('resellerToken');
    if (t) {
      loadProfile();
      loadProducts();
      loadOrders();
      loadTopups();
      const i = setInterval(() => { loadProfile(); loadOrders(); loadProducts(); loadTopups(); }, 10000);
      return () => clearInterval(i);
    }
  }, [loadProfile, loadProducts, loadOrders, loadTopups]);

  // Handle topup return from Stripe
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('topup') === 'success') {
      setTab('topups');
      setTopMsg({ type: 'ok', text: 'Payment received! Your balance will update shortly.' });
      loadProfile();
      loadTopups();
      window.history.replaceState({}, '', '/reseller');
    } else if (params.get('topup') === 'cancelled') {
      setTab('addbal');
      setTopMsg({ type: 'err', text: 'Payment was cancelled.' });
      window.history.replaceState({}, '', '/reseller');
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginError('');
    try {
      const res = await fetch(`${BASE}/api/reseller/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(loginForm)
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error);
      localStorage.setItem('resellerToken', data.token);
      setReseller(data.reseller);
      loadProducts();
      loadOrders();
    } catch (err) {
      setLoginError(err.message);
    }
  };

  const handleLogout = () => {
    setReseller(null);
    localStorage.removeItem('resellerToken');
    navigate('/reseller');
  };

  const handleOrder = async (e) => {
    e.preventDefault();
    setOrderLoading(true);
    setOrderResult(null);
    try {
      const res = await RAPI('/api/reseller/order', {
        method: 'POST',
        body: JSON.stringify(orderForm)
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error);
      setLastOrderId(data.orderId);
      setOrderResult({ type: 'ok', text: 'Order placed! Boosts are being delivered.' });
      setReseller(prev => prev ? { ...prev, balance: data.balance } : prev);
      loadOrders();
      loadProfile();
    } catch (err) {
      setOrderResult({ type: 'err', text: err.message });
    } finally { setOrderLoading(false); }
  };

  const handleTopup = async (e) => {
    e.preventDefault();
    setTopMsg(null);
    setTopLoading(true);
    const amt = parseFloat(topAmt);
    if (!amt || amt < 1 || amt > 5000) { setTopMsg({ type: 'err', text: 'Amount must be $1-$5000' }); setTopLoading(false); return; }

    try {
      if (topMethod === 'stripe') {
        if (amt < 1) { setTopMsg({ type: 'err', text: 'Minimum top-up for card payment is $1.00' }); setTopLoading(false); return; }
        const res = await RAPI('/api/reseller/stripe-topup', { method: 'POST', body: JSON.stringify({ amount: amt }) });
        const data = await res.json();
        if (!res.ok) throw new Error(data.error);
        window.location.href = data.url;
      } else {
        const res = await RAPI('/api/reseller/topup', { method: 'POST', body: JSON.stringify({ amount: amt, cryptoType: topCrypto }) });
        const data = await res.json();
        if (!res.ok) throw new Error(data.error);
        window.open(`/checkout/${data.orderId}`, '_blank');
        setTopMsg({ type: 'ok', text: 'Checkout opened — balance will update after payment' });
        loadTopups();
      }
    } catch (err) {
      setTopMsg({ type: 'err', text: err.message });
    } finally { setTopLoading(false); }
  };

  const greet = () => {
    const h = new Date().getHours();
    return h < 12 ? 'Good morning' : h < 18 ? 'Good afternoon' : 'Good evening';
  };

  const selectedProduct = products.find(p => p.id === orderForm.productId);
  const orderTotal = selectedProduct ? (orderForm.quantity * selectedProduct.pricePerBoost).toFixed(2) : '0.00';

  const statusBadge = (s) => {
    const cls = s === 'completed' ? 'db-bg-ok' : s === 'failed' ? 'db-bg-err' : s === 'processing' ? 'db-bg-info' : 'db-bg-warn';
    return <span className={`db-badge ${cls}`}>{s || 'pending'}</span>;
  };

  // ===== LOGIN SCREEN =====
  if (!reseller) {
    return (
      <div className="db-layout" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ maxWidth: 400, width: '100%', padding: '1.5rem', position: 'relative', zIndex: 1 }}>
          <div style={{ textAlign: 'center', marginBottom: '1.5rem' }}>
            <a href="/" style={{ display: 'inline-flex', alignItems: 'center', gap: '0.5rem', textDecoration: 'none', marginBottom: '0.75rem' }}>
              <img src="/logo.png" alt="" style={{ width: 28, height: 28, borderRadius: 6, objectFit: 'contain' }} />
              <span style={{ fontSize: '1rem', fontWeight: 600, background: 'linear-gradient(135deg, #00e5d0 0%, #f5a623 50%, #e84393 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>Outsourced</span>
            </a>
            <h1 style={{ fontSize: '1.15rem', fontWeight: 500, marginBottom: '0.2rem' }}>Reseller Panel</h1>
            <p style={{ fontSize: '0.78rem', color: 'var(--text-muted)' }}>Sign in to manage your orders</p>
          </div>
          <div className="db-form-card" style={{ maxWidth: '100%' }}>
            <form onSubmit={handleLogin}>
              {loginError && <div className="db-msg err" style={{ marginTop: 0, marginBottom: '0.75rem' }}>{loginError}</div>}
              <div className="db-field">
                <label>Email</label>
                <input type="email" value={loginForm.email} onChange={e => setLoginForm({ ...loginForm, email: e.target.value })} className="db-input" required placeholder="you@example.com" />
              </div>
              <div className="db-field">
                <label>Password</label>
                <input type="password" value={loginForm.password} onChange={e => setLoginForm({ ...loginForm, password: e.target.value })} className="db-input" required placeholder="••••••••" />
              </div>
              <button type="submit" className="db-submit">Sign In</button>
            </form>
          </div>
          <div style={{ textAlign: 'center', marginTop: '1rem' }}>
            <a href="/" style={{ color: 'var(--text-muted)', fontSize: '0.75rem', textDecoration: 'none' }}>← Back to store</a>
          </div>
        </div>
      </div>
    );
  }

  // ===== DASHBOARD =====
  return (
    <div className="db-layout">
      {/* Top Nav */}
      <div className="db-topnav">
        <div className="db-topnav-inner">
          <div className="db-topnav-left">
            <a href="/" className="db-topnav-brand">
              <img src="/logo.png" alt="" style={{ width: 22, height: 22, borderRadius: 5, objectFit: 'contain' }} />
              Outsourced
            </a>
            <a href="/" className="db-topnav-link">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="m3 9 9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" /></svg>
              Home
            </a>
            <a href="/reseller" className="db-topnav-link active">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="3" width="7" height="9" /><rect x="14" y="3" width="7" height="5" /><rect x="14" y="12" width="7" height="9" /><rect x="3" y="16" width="7" height="5" /></svg>
              Reseller
            </a>
          </div>
          <div className="db-topnav-right">
            <span style={{ fontSize: '0.7rem', color: 'var(--accent)', background: 'var(--accent-glow)', padding: '0.2rem 0.55rem', borderRadius: 4, border: '1px solid rgba(6,244,173,0.15)', fontWeight: 500 }}>{reseller.discount}% off</span>
            <span className="db-balance-pill">Balance: ${(reseller.balance || 0).toFixed(2)}</span>
            <div className="db-avatar" onClick={handleLogout} title="Sign out">
              {(reseller.name || '?')[0].toUpperCase()}
            </div>
          </div>
        </div>
      </div>

      <div className="db-body">
        {/* Sidebar */}
        <div className="db-sidebar">
          <button className={`db-sidenav ${tab === 'home' ? 'on' : ''}`} onClick={() => setTab('home')}>Home</button>
          <button className={`db-sidenav ${tab === 'buy' ? 'on' : ''}`} onClick={() => setTab('buy')}>Place Order</button>
          <button className={`db-sidenav ${tab === 'orders' ? 'on' : ''}`} onClick={() => setTab('orders')}>
            Order History
            {orders.filter(o => o.boostStatus === 'processing').length > 0 && <span className="db-notif">{orders.filter(o => o.boostStatus === 'processing').length}</span>}
          </button>
          <button className={`db-sidenav ${tab === 'topups' ? 'on' : ''}`} onClick={() => setTab('topups')}>
            Top-ups
            {topups.filter(t => t.status === 'waiting').length > 0 && <span className="db-notif">{topups.filter(t => t.status === 'waiting').length}</span>}
          </button>
          <button className={`db-sidenav ${tab === 'addbal' ? 'on' : ''}`} onClick={() => setTab('addbal')}>Add Balance</button>
          <button className={`db-sidenav ${tab === 'api' ? 'on' : ''}`} onClick={() => setTab('api')}>API Access</button>
        </div>

        {/* Mobile Tab Bar */}
        <div className="db-mobile-tabs">
          <button className={`db-mtab ${tab === 'home' ? 'on' : ''}`} onClick={() => setTab('home')}>Home</button>
          <button className={`db-mtab ${tab === 'buy' ? 'on' : ''}`} onClick={() => setTab('buy')}>Buy</button>
          <button className={`db-mtab ${tab === 'orders' ? 'on' : ''}`} onClick={() => setTab('orders')}>Orders</button>
          <button className={`db-mtab ${tab === 'addbal' ? 'on' : ''}`} onClick={() => setTab('addbal')}>Top Up</button>
          <button className={`db-mtab ${tab === 'api' ? 'on' : ''}`} onClick={() => setTab('api')}>API</button>
        </div>

        {/* Main Content */}
        <div className="db-main">

          {/* HOME TAB */}
          {tab === 'home' && (
            <>
              <h1 className="db-greeting">{greet()}, {reseller.name}</h1>
              <p className="db-sub">Reseller dashboard — {reseller.discount}% discount on all products</p>
              <div className="db-divider"></div>

              <div className="db-stats-row" style={{ gridTemplateColumns: 'repeat(4, 1fr)' }}>
                <div className="db-stat-card">
                  <div className="db-stat-label">Balance</div>
                  <div className="db-stat-val accent">${(reseller.balance || 0).toFixed(2)}</div>
                </div>
                <div className="db-stat-card">
                  <div className="db-stat-label">Total Spent</div>
                  <div className="db-stat-val">${(reseller.totalSpent || 0).toFixed(2)}</div>
                </div>
                <div className="db-stat-card">
                  <div className="db-stat-label">Total Orders</div>
                  <div className="db-stat-val">{reseller.totalOrders || 0}</div>
                </div>
                <div className="db-stat-card">
                  <div className="db-stat-label">Boosts Sold</div>
                  <div className="db-stat-val">{reseller.totalBoosts || 0}</div>
                </div>
              </div>

              {/* Prices */}
              <div className="db-section-head">
                <h2>Your Prices</h2>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '0.75rem', marginBottom: '1.75rem' }}>
                {products.map(p => (
                  <div key={p.id} className="db-stat-card">
                    <div className="db-stat-label">{p.name}</div>
                    <div style={{ display: 'flex', alignItems: 'baseline', gap: '0.4rem' }}>
                      <span className="db-stat-val accent">${p.pricePerBoost.toFixed(3)}</span>
                      <span style={{ fontSize: '0.7rem', color: 'var(--text-muted)', textDecoration: 'line-through' }}>${p.originalPrice?.toFixed(3)}</span>
                    </div>
                    <span style={{ display: 'block', marginTop: '0.25rem' }}>{p.stock > 0 ? <span className="db-field-hint db-stock">{p.stock} in stock</span> : <span style={{ fontSize: '0.7rem', color: 'var(--red-primary)' }}>Out of stock</span>}</span>
                  </div>
                ))}
              </div>

              {/* Recent Orders */}
              <div className="db-section-head">
                <h2>Recent Orders</h2>
                <button className="db-link-btn" onClick={() => setTab('orders')}>Show All</button>
              </div>
              <div className="db-table-wrap">
                <table className="db-table">
                  <thead>
                    <tr><th>Order ID</th><th>Product</th><th>Qty</th><th>Amount</th><th>Status</th><th>Date</th></tr>
                  </thead>
                  <tbody>
                    {orders.length === 0 ? (
                      <tr><td colSpan="6" className="db-empty">No orders yet — place your first order!</td></tr>
                    ) : orders.slice(0, 5).map(o => (
                      <tr key={o.id}>
                        <td className="db-mono">{o.id.slice(0, 8)}…</td>
                        <td>{o.productName || '—'}</td>
                        <td>{o.quantity}x</td>
                        <td>${(o.total || 0).toFixed(2)}</td>
                        <td>{statusBadge(o.boostStatus)}</td>
                        <td className="db-muted">{o.createdAt ? new Date(o.createdAt).toLocaleDateString() : ''}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}

          {/* PLACE ORDER TAB */}
          {tab === 'buy' && (
            <>
              <h1 className="db-greeting">Place Order</h1>
              <p className="db-sub">Order boosts at your discounted reseller pricing</p>
              <div className="db-divider"></div>

              <div className="db-form-card">
                <form onSubmit={handleOrder}>
                  <div className="db-field">
                    <label>Server Invite</label>
                    <input type="text" value={orderForm.serverInvite} onChange={e => setOrderForm({ ...orderForm, serverInvite: e.target.value })} placeholder="https://discord.gg/..." className="db-input" required />
                  </div>
                  <div className="db-row2">
                    <div className="db-field">
                      <label>Amount</label>
                      <div className="db-stepper">
                        <button type="button" className="db-step-btn" onClick={() => setOrderForm(f => ({ ...f, quantity: Math.max(2, f.quantity - 2) }))}>−</button>
                        <input type="number" value={orderForm.quantity} onChange={e => setOrderForm({ ...orderForm, quantity: Math.max(2, parseInt(e.target.value) || 2) })} className="db-step-input" min="2" max="30" step="2" />
                        <button type="button" className="db-step-btn" onClick={() => setOrderForm(f => ({ ...f, quantity: Math.min(30, f.quantity + 2) }))}>+</button>
                      </div>
                      <span className="db-field-hint">1 quantity = 1x boost</span>
                      {orderForm.quantity % 2 !== 0 && <span className="db-field-warn">⚠ Orders must be even quantities</span>}
                    </div>
                    <div className="db-field">
                      <label>Duration</label>
                      <select value={orderForm.productId} onChange={e => setOrderForm({ ...orderForm, productId: e.target.value })} className="db-input" required>
                        <option value="">Select...</option>
                        {products.map(p => (
                          <option key={p.id} value={p.id}>{p.name} — ${p.pricePerBoost.toFixed(3)}/boost</option>
                        ))}
                      </select>
                      {selectedProduct && <span className="db-field-hint db-stock">{selectedProduct.stock} in stock</span>}
                    </div>
                  </div>

                  <div className="db-price-box">
                    <div className="db-price-row">
                      <span>Per boost:</span>
                      <span>{selectedProduct ? `$${selectedProduct.pricePerBoost.toFixed(3)}` : '—'}</span>
                    </div>
                    {selectedProduct && (
                      <div className="db-price-row">
                        <span>Retail price:</span>
                        <span style={{ textDecoration: 'line-through', color: 'var(--text-muted)' }}>${(orderForm.quantity * (selectedProduct.originalPrice || 0)).toFixed(2)}</span>
                      </div>
                    )}
                    <div className="db-price-row">
                      <span>Your savings:</span>
                      <span style={{ color: 'var(--accent)' }}>{selectedProduct ? `-$${((orderForm.quantity * (selectedProduct.originalPrice || 0)) - parseFloat(orderTotal)).toFixed(2)}` : '—'}</span>
                    </div>
                    <div className="db-price-row db-price-total">
                      <span>Total:</span>
                      <span>${orderTotal}</span>
                    </div>
                  </div>

                  <button type="submit" className="db-submit" disabled={orderLoading || !orderForm.productId || orderForm.quantity % 2 !== 0}>
                    {orderLoading ? 'Processing...' : 'Place Order'}
                  </button>

                  {orderResult && (
                    <div className={`db-msg ${orderResult.type}`}>
                      {orderResult.text}
                      {lastOrderId && orderResult.type === 'ok' && (
                        <div className="db-order-id">
                          <span>Order ID: </span>
                          <code>{lastOrderId}</code>
                          <button type="button" className="db-copy-btn" onClick={() => navigator.clipboard.writeText(lastOrderId)}>Copy</button>
                        </div>
                      )}
                    </div>
                  )}
                </form>
              </div>
            </>
          )}

          {/* ORDER HISTORY TAB */}
          {tab === 'orders' && (
            <>
              <h1 className="db-greeting">Order History</h1>
              <p className="db-sub">All orders placed through your reseller account</p>
              <div className="db-divider"></div>

              <div className="db-stats-row">
                <div className="db-stat-card">
                  <div className="db-stat-label">Total</div>
                  <div className="db-stat-val">{orders.length}</div>
                </div>
                <div className="db-stat-card">
                  <div className="db-stat-label">Completed</div>
                  <div className="db-stat-val accent">{orders.filter(o => o.boostStatus === 'completed').length}</div>
                </div>
                <div className="db-stat-card">
                  <div className="db-stat-label">Failed</div>
                  <div className="db-stat-val" style={{ color: orders.filter(o => o.boostStatus === 'failed').length > 0 ? 'var(--red-primary)' : undefined }}>{orders.filter(o => o.boostStatus === 'failed').length}</div>
                </div>
              </div>

              <div className="db-table-wrap">
                <table className="db-table">
                  <thead>
                    <tr><th>Order ID</th><th>Server</th><th>Product</th><th>Qty</th><th>Amount</th><th>Boost Status</th><th>Date</th></tr>
                  </thead>
                  <tbody>
                    {orders.length === 0 ? (
                      <tr><td colSpan="7" className="db-empty">No orders yet</td></tr>
                    ) : orders.map(o => (
                      <tr key={o.id}>
                        <td className="db-mono">{o.id.slice(0, 8)}…</td>
                        <td className="db-mono">{(o.serverInvite || '').slice(0, 12)}{(o.serverInvite || '').length > 12 ? '…' : ''}</td>
                        <td>{o.productName || '—'}</td>
                        <td>{o.quantity}x</td>
                        <td>${(o.total || 0).toFixed(2)}</td>
                        <td>{statusBadge(o.boostStatus)}</td>
                        <td className="db-muted">{o.createdAt ? new Date(o.createdAt).toLocaleDateString() : ''}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}

          {/* TOP-UPS HISTORY TAB */}
          {tab === 'topups' && (
            <>
              <h1 className="db-greeting">Top-up History</h1>
              <p className="db-sub">Balance deposits and their status</p>
              <div className="db-divider"></div>

              <div className="db-table-wrap">
                <table className="db-table">
                  <thead>
                    <tr><th>Order ID</th><th>Amount</th><th>Method</th><th>Status</th><th>Date</th></tr>
                  </thead>
                  <tbody>
                    {topups.length === 0 ? (
                      <tr><td colSpan="5" className="db-empty">No top-ups yet</td></tr>
                    ) : topups.map(t => (
                      <tr key={t.id}>
                        <td className="db-mono">{t.id.slice(0, 8)}…</td>
                        <td>${(t.subtotal || t.total || 0).toFixed(2)}</td>
                        <td>{t.paymentMethod === 'stripe' ? 'Card' : (t.cryptoType || 'Crypto')}</td>
                        <td>{statusBadge(t.status)}</td>
                        <td className="db-muted">{t.createdAt ? new Date(t.createdAt).toLocaleDateString() : ''}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div style={{ textAlign: 'center', marginTop: '1.25rem' }}>
                <button className="db-link-btn" onClick={() => setTab('addbal')}>+ Add Balance</button>
              </div>
            </>
          )}

          {/* ADD BALANCE TAB */}
          {tab === 'addbal' && (
            <>
              <h1 className="db-greeting">Add Balance</h1>
              <p className="db-sub">Top up your reseller balance to place orders</p>
              <div className="db-divider"></div>

              <div className="db-form-card">
                <form onSubmit={handleTopup}>
                  <div className="db-field">
                    <label>Amount (USD)</label>
                    <input type="number" value={topAmt} onChange={e => setTopAmt(e.target.value)} placeholder="Enter amount..." className="db-input" min="1" max="5000" step="0.01" required />
                    <span className="db-field-hint">Minimum $1.00 — Maximum $5,000.00</span>
                  </div>

                  <div className="db-field">
                    <label>Quick amounts</label>
                    <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap' }}>
                      {[10, 25, 50, 100, 250, 500].map(a => (
                        <button key={a} type="button" onClick={() => setTopAmt(String(a))} className="db-copy-btn" style={{ padding: '0.45rem 0.85rem', fontSize: '0.75rem', fontWeight: 600, background: String(a) === topAmt ? 'var(--accent-glow)' : undefined, borderColor: String(a) === topAmt ? 'var(--accent)' : undefined, color: String(a) === topAmt ? 'var(--accent)' : undefined }}>
                          ${a}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="db-field">
                    <label>Payment Method</label>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.5rem' }}>
                      <button type="button" onClick={() => setTopMethod('stripe')} style={{ padding: '0.75rem', borderRadius: 10, border: topMethod === 'stripe' ? '2px solid var(--accent)' : '1px solid rgba(255,255,255,0.08)', background: topMethod === 'stripe' ? 'var(--accent-glow)' : 'rgba(255,255,255,0.03)', cursor: 'pointer', textAlign: 'center', color: 'var(--text-primary)', fontSize: '0.82rem', fontWeight: 600, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.4rem' }}>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="1" y="4" width="22" height="16" rx="2" ry="2"/><line x1="1" y1="10" x2="23" y2="10"/></svg>
                        Card
                      </button>
                      <button type="button" onClick={() => setTopMethod('crypto')} style={{ padding: '0.75rem', borderRadius: 10, border: topMethod === 'crypto' ? '2px solid var(--accent)' : '1px solid rgba(255,255,255,0.08)', background: topMethod === 'crypto' ? 'var(--accent-glow)' : 'rgba(255,255,255,0.03)', cursor: 'pointer', textAlign: 'center', color: 'var(--text-primary)', fontSize: '0.82rem', fontWeight: 600, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.4rem' }}>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>
                        Crypto
                      </button>
                    </div>
                  </div>

                  {topMethod === 'crypto' && (
                    <div className="db-field">
                      <label>Cryptocurrency</label>
                      <select value={topCrypto} onChange={e => setTopCrypto(e.target.value)} className="db-input">
                        <option value="LTC">Litecoin (LTC)</option>
                        <option value="BTC">Bitcoin (BTC)</option>
                        <option value="ETH">Ethereum (ETH)</option>
                        <option value="USDT_ERC20">USDT (ERC20)</option>
                        <option value="USDC_ERC20">USDC (ERC20)</option>
                        <option value="USDT_BEP20">USDT (BEP20)</option>
                      </select>
                    </div>
                  )}

                  <div className="db-price-box">
                    <div className="db-price-row">
                      <span>Top-up amount:</span>
                      <span>${topAmt ? parseFloat(topAmt).toFixed(2) : '0.00'}</span>
                    </div>
                    {topMethod === 'stripe' && parseFloat(topAmt) > 0 && parseFloat(topAmt) < 5 && (
                      <div className="db-price-row">
                        <span>Card processing fee:</span>
                        <span style={{ color: 'var(--text-muted)' }}>$0.50</span>
                      </div>
                    )}
                    <div className="db-price-row db-price-total">
                      <span>Balance added:</span>
                      <span style={{ color: 'var(--accent)' }}>+${topAmt ? parseFloat(topAmt).toFixed(2) : '0.00'}</span>
                    </div>
                  </div>

                  <button type="submit" className="db-submit" disabled={topLoading || !topAmt || parseFloat(topAmt) < 1}>
                    {topLoading ? 'Processing...' : topMethod === 'stripe' ? 'Pay with Card' : 'Pay with Crypto'}
                  </button>

                  {topMsg && (
                    <div className={`db-msg ${topMsg.type}`} style={{ marginTop: '0.75rem' }}>
                      {topMsg.text}
                    </div>
                  )}
                </form>
              </div>

              <div style={{ marginTop: '1rem', padding: '0.85rem 1rem', background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.05)', borderRadius: 10, fontSize: '0.72rem', color: 'var(--text-muted)', lineHeight: 1.6 }}>
                <strong style={{ color: 'var(--text-secondary)' }}>ℹ How it works:</strong> Card payments are credited instantly after Stripe confirms. Crypto payments are credited after blockchain confirmation (usually 5–30 minutes). Your balance is used when placing boost orders.
              </div>
            </>
          )}

          {/* API ACCESS TAB */}
          {tab === 'api' && (
            <>
              <h1 className="db-greeting">API Access</h1>
              <p className="db-sub">Automate boost orders with our REST API</p>
              <div className="db-divider"></div>

              <div className="db-form-card" style={{ maxWidth: '100%' }}>
                <div className="db-field">
                  <label>Your API Key</label>
                  <div style={{ display: 'flex', gap: '0.4rem' }}>
                    <input type={showApiKey ? 'text' : 'password'} value={reseller.apiKey || ''} readOnly className="db-input" style={{ flex: 1, fontFamily: 'monospace', fontSize: '0.75rem' }} />
                    <button type="button" className="db-copy-btn" style={{ padding: '0.5rem 0.75rem', fontSize: '0.72rem', whiteSpace: 'nowrap' }} onClick={() => setShowApiKey(!showApiKey)}>
                      {showApiKey ? 'Hide' : 'Show'}
                    </button>
                    <button type="button" className="db-copy-btn" style={{ padding: '0.5rem 0.75rem', fontSize: '0.72rem' }} onClick={() => { navigator.clipboard.writeText(reseller.apiKey); setKeyCopied(true); setTimeout(() => setKeyCopied(false), 2000); }}>
                      {keyCopied ? '✓ Copied' : 'Copy'}
                    </button>
                  </div>
                  <span className="db-field-hint">Include as <code style={{ background: 'rgba(255,255,255,0.04)', padding: '1px 4px', borderRadius: 3, fontSize: '0.68rem' }}>X-API-Key</code> header in all requests</span>
                </div>
              </div>

              <div style={{ marginTop: '1.25rem', display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                {[
                  {
                    method: 'GET', path: '/api/reseller/products', title: 'Get Products',
                    desc: 'Returns available products with your discounted pricing.',
                    response: `{
  "products": [
    { "id": "boost_1m", "name": "1 Month", "pricePerBoost": ${selectedProduct ? selectedProduct.pricePerBoost.toFixed(3) : '0.170'}, "stock": 2598 }
  ],
  "discount": ${reseller.discount}
}`
                  },
                  {
                    method: 'POST', path: '/api/reseller/order', title: 'Place Order',
                    desc: 'Create a boost order. Deducts from your balance and delivers automatically.',
                    body: `{
  "serverInvite": "discord.gg/abc123",
  "productId": "boost_1m",
  "quantity": 14
}`,
                    response: `{
  "success": true,
  "orderId": "abc-123-...",
  "total": 2.38,
  "balance": 47.62
}`
                  },
                  {
                    method: 'GET', path: '/api/reseller/order/:orderId', title: 'Get Order Status',
                    desc: 'Check the status and delivery result of a specific order.',
                    response: `{
  "id": "abc-123",
  "boostStatus": "completed",
  "quantity": 14,
  "total": 2.38,
  "boostLog": { "successCount": 7, "failedCount": 0 }
}`
                  },
                  {
                    method: 'GET', path: '/api/reseller/orders', title: 'List All Orders',
                    desc: 'Get all your orders sorted by most recent.',
                    response: `[{ "id": "...", "boostStatus": "completed", ... }]`
                  }
                ].map((ep, i) => (
                  <div key={i} className="db-form-card" style={{ maxWidth: '100%' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
                      <span className={`db-badge ${ep.method === 'GET' ? 'db-bg-ok' : 'db-bg-warn'}`} style={{ fontFamily: 'monospace', fontWeight: 600, letterSpacing: '0.5px' }}>{ep.method}</span>
                      <code style={{ fontSize: '0.78rem', color: 'var(--text-primary)' }}>{ep.path}</code>
                    </div>
                    <h3 style={{ fontSize: '0.85rem', fontWeight: 500, marginBottom: '0.25rem' }}>{ep.title}</h3>
                    <p style={{ fontSize: '0.78rem', color: 'var(--text-muted)', marginBottom: '0.65rem' }}>{ep.desc}</p>

                    {ep.body && (
                      <div className="db-field" style={{ marginBottom: '0.65rem' }}>
                        <label>Request Body</label>
                        <pre style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.05)', borderRadius: 7, padding: '0.75rem', fontSize: '0.72rem', color: 'var(--orb-orange)', overflow: 'auto', fontFamily: 'monospace', margin: 0, lineHeight: 1.5 }}>{ep.body}</pre>
                      </div>
                    )}
                    <div className="db-field" style={{ marginBottom: 0 }}>
                      <label>Response</label>
                      <pre style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.05)', borderRadius: 7, padding: '0.75rem', fontSize: '0.72rem', color: 'var(--accent)', overflow: 'auto', fontFamily: 'monospace', margin: 0, lineHeight: 1.5 }}>{ep.response}</pre>
                    </div>
                  </div>
                ))}

                <div className="db-form-card" style={{ maxWidth: '100%' }}>
                  <h3 style={{ fontSize: '0.85rem', fontWeight: 500, marginBottom: '0.5rem' }}>Example (cURL)</h3>
                  <pre style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.05)', borderRadius: 7, padding: '0.75rem', fontSize: '0.72rem', color: 'var(--text-primary)', overflow: 'auto', fontFamily: 'monospace', lineHeight: 1.6, margin: 0 }}>
{`curl -X POST ${window.location.origin}/api/reseller/order \\
  -H "Content-Type: application/json" \\
  -H "X-API-Key: ${showApiKey ? reseller.apiKey : 'your_api_key'}" \\
  -d '{"serverInvite":"discord.gg/abc","productId":"boost_1m","quantity":14}'`}
                  </pre>
                </div>
              </div>
            </>
          )}

        </div>
      </div>
    </div>
  );
};

export default ResellerPanel;
